<script setup lang="ts">
  import { app } from '@/composition/app'
  import { GroupState } from '@/composition/groups'
  import { UserState } from '@/composition/users'
  import { IComments } from '@/models/Comments'
  import { GroupMemberRole } from '@/models/GroupMember'
  import { IPosts } from '@/models/Posts'
  import { useGlobalStore } from '@/store/app'
  import { UICard } from '@gohighlevel/ghl-ui'
  import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, PropType, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { emitter } from '../../../main'
  import PostViewBody from './PostViewBody.vue'
  import PostViewFooter from './PostViewFooter.vue'
  import PostViewHeader from './PostViewHeader.vue'

  const CommentEditor = defineAsyncComponent(() => import('@/components/comments/CommentEditor.vue'))
  const AttachmentsPreview = defineAsyncComponent(() => import('@/components/common/AttachmentsPreview.vue'))
  // Presentational component driven by props from PostViewRouter

  const route = useRoute()
  const router = useRouter()
  const emit = defineEmits<{
    (e: 'action-selected', action: string): void
    (e: 'likes-clicked'): void
    (e: 'like'): void
    (e: 'load-more-comments'): void
  }>()

  const props = defineProps({
    showLikesModal: { type: Boolean, required: false },
    isPostLoading: { type: Boolean, required: false },
    post: {
      type: Object as PropType<IPosts>,
      required: false,
    },
    authorRole: {
      type: String as PropType<GroupMemberRole>,
      required: false,
    },
    channelName: { type: String, required: false },
    comments: {
      type: Array as PropType<IComments[]>,
      required: false,
    },
    fetchingComments: { type: Boolean, required: false },
    loadingMoreComments: { type: Boolean, required: false },
    commentsNextPage: { type: Boolean, required: false },
    postActionOptions: {
      type: Array as () => Array<{
        label: string
        key: string
        class: string
        icon: () => any
      }>,
      required: false,
    },
  })
  const showEditor = ref(true)
  const showPostPreview = ref(false)
  const currentPostIndex = ref(0)
  const appStore = useGlobalStore()

  const isEditorVisibleToUser = computed(() => {
    return appStore.isAuthenticated && GroupState.isActiveMember()
  })

  function updatePreview(index: number) {
    currentPostIndex.value = index
    showPostPreview.value = true
  }

  const handleChannelClick = (channelSlug: string) => {
    router.push({ name: 'channel', params: { channelSlug } })
  }

  const handleBack = () => {
    router.back()
  }

  function handleLike() {
    emit('like')
  }

  function handleLoadMoreComments() {
    emit('load-more-comments')
  }

  function forwardActionSelected(action: any) {
    const key = action?.key ?? action
    emit('action-selected', key)
  }

  onMounted(() => {
    // Add event listener to show footer comment editor when reply editors are closed
    emitter.on('show:footer-comment-editor', () => {
      showEditor.value = true
    })
  })

  onBeforeUnmount(() => {
    // Remove event listeners
    emitter.off('show:footer-comment-editor')
  })
</script>

<template>
  <div v-if="!app.state.isMobile" id="container_group_discussion">
    <UICard class="post-view-card relative flex h-[calc(100vh-150px)] flex-col items-stretch rounded-xl bg-communities-background">
      <PostViewHeader
        :post="post"
        :channel-slug="post?.channel?.slug"
        :author-role="authorRole"
        :channel-name="channelName"
        :post-action-options="postActionOptions"
        :loading="isPostLoading"
        @back="handleBack"
        @channel-click="handleChannelClick"
        @action-selected="forwardActionSelected"
      />
      <PostViewBody
        :post="post"
        :comment-editor-bottom="0"
        :comments="comments"
        :fetching-comments="fetchingComments"
        :loading-more-comments="loadingMoreComments"
        :comments-next-page="commentsNextPage"
        :show-likes-modal="showLikesModal"
        :loading="isPostLoading"
        @likes-clicked="$emit('likes-clicked')"
        @update-preview="updatePreview"
        @like="handleLike"
        @load-more-comments="handleLoadMoreComments"
      />

      <PostViewFooter class="sticky" :loading="isPostLoading">
        <template #comment-editor>
          <CommentEditor v-if="showEditor && isEditorVisibleToUser" :user="UserState.state.currentUser" :comment-editor-bottom="0" />
        </template>
      </PostViewFooter>
    </UICard>
    <AttachmentsPreview
      :show-modal="showPostPreview"
      :file="post?.attachments && post.attachments[currentPostIndex]"
      :current-index="currentPostIndex"
      :total-attachments="post?.attachments?.length"
      @update:close="showPostPreview = false"
      @update:show="updatePreview"
    />
  </div>
</template>

<style scoped>
  .sticky_scroll {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: sticky;
  }

  .post-content-container {
    border-radius: 0.5rem;
    background-color: white;
    padding: 1rem;
  }

  .sidebar {
    min-width: 250px;
  }

  .mobile-content-container {
    height: calc(100vh - 46.8px);
  }

  @media (max-width: 930px) {
    .outer-container {
      padding: 0px !important;
      padding-bottom: 75px !important;
    }
  }

  .fade-transition {
    transition: opacity 0.3s ease-in-out;
    opacity: 1;
  }

  .fade-transition[v-cloak],
  .fade-transition:not([v-if]) {
    opacity: 0;
  }
  :deep(.post-view-card .hl-card-content) {
    padding: 24px 12px !important;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  :deep(.post-view-body) {
    flex: 1;
    min-height: 0; /* Important for Firefox */
    display: flex;
    flex-direction: column;
  }

  :deep(.post-view-body #comments_cards) {
    flex: 1;
    overflow-y: auto;
    padding-bottom: 16px;
  }

  /* :deep(#post-comment-editor) {
  position: sticky !important;
  bottom: 0;
  left: 0;
  right: 0;

  border-top: 1px solid var(--communities-border);
  padding: 16px 0;
  margin: 0 !important;
  z-index: 11;
} */
</style>
